import { db } from "../config/firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  increment,
  arrayUnion,
  setDoc,
  getDocs,
  where,
} from "firebase/firestore";
import bcrypt from "bcryptjs";

export const getNodeById = async (nodeId) => {
  const docRef = doc(db, "nodes", nodeId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data();
  } else {
    throw new Error("ノードが見つかりません");
  }
};

/**
 * 新しいノード・選択肢の追加時
 */
export const addNode = async (storyId, nodeData) => {
  if (!storyId || !nodeData) return;

  const nodeRef = await addDoc(collection(db, "stories", storyId, "nodes"), {
    ...nodeData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    votes: nodeData.votes || 0,
    voters: nodeData.voters || [],
    status: nodeData.status || [], // 配列で初期化
    childrenIds: [], // 下位ノードIDを格納する配列を追加
  });
  if (nodeData.parentId) {
    const parentRef = doc(db, "stories", storyId, "nodes", nodeData.parentId);
    await updateDoc(parentRef, {
      childrenIds: arrayUnion(nodeRef.id),
    });
  }

  return nodeRef.id;
};

/**
 * ストーリーの現在ラウンドと現在本文を更新
 * updateStoryRoundAndContent
 */
export const updateStoryRoundAndContent = async (
  storyId,
  round,
  newContent,
  parentId
) => {
  if (!storyId || !newContent) return;
  const storyRef = doc(db, "stories", storyId);

  // 現在の本文を取得して追記
  const snap = await getDoc(storyRef);
  if (!snap.exists()) return;

  const prevContent = snap.data().currentNodeContent || "";
  const updatedContent = prevContent
    ? `${prevContent}\n${newContent}`
    : newContent;

  await updateDoc(storyRef, {
    currentRound: round + 1,
    currentNodeContent: updatedContent,
    currentParentId: parentId || null, // ← 追加
    updatedAt: serverTimestamp(),
  });

  /*console.log(
    "ストーリー更新: currentRound =",
    round + 1,
    "currentParentId =",
    parentId
  );*/
};

/**
 * ノードのリアルタイム購読
 */
export const subscribeNodes = (storyId, callback) => {
  if (!storyId) return () => {};
  const q = query(
    collection(db, "stories", storyId, "nodes"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snapshot) => {
    const nodes = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(nodes);
  });
};

/**
 * ノードに投票
 */
export const voteNode = async (storyId, nodeId, userId) => {
  if (!storyId || !nodeId || !userId) return;
  const nodeRef = doc(db, "stories", storyId, "nodes", nodeId);
  const snap = await getDoc(nodeRef);
  if (!snap.exists()) {
    console.error("投票対象のノードが存在しません", nodeId);
    return;
  }
  await updateDoc(nodeRef, {
    votes: increment(1),
    voters: arrayUnion(userId),
  });
};

/**
 * 新しいスレッド作成と最初のノード
 * @param {Object} params
 * @param {string} params.title - スレッドタイトル
 * @param {string} params.content - 最初の投稿内容
 * @param {string} params.category - カテゴリ名
 * @param {string} params.threadType - スレッドタイプ
 * @param {string} params.voteMethod - 投票方式
 * @param {number} params.voteTime - 投票時間
 * @param {string} params.createdBy - 作成者ID
 * @param {string} [params.baseStoryId] - 引用元ストーリーID（再開時に使用）
 * @param {Object} options
 * @param {string} [options.baseStoryId] 元のストーリーID（再開時のみ）
 * @param {string} [options.parentNodeId] 再開元ノードID（再開時のみ）
 */
export const createThreadWithFirstNode = async ({
  title,
  content,
  category,
  threadType,
  voteMethod,
  voteTime,
  createdBy,
  baseStoryId = null,
  parentNodeId = null,
  resumeFirstNodeId = null,
  password, // ここには平文パスワード or 既にハッシュ化済みパスワードが入る
}) => {
  try {
    const storyRef = doc(collection(db, "stories"));
    const storyId = storyRef.id;

    // 🔒 ハッシュ済みかどうか判定して再ハッシュを防ぐ
    const hashedPassword =
      password && password.startsWith("$2b$")
        ? password // 既にハッシュ済み
        : password
        ? await hashPassword(password) // 平文の場合のみハッシュ化
        : null;

    // stories に基本情報を保存
    await setDoc(storyRef, {
      title,
      category,
      threadType,
      voteMethod,
      voteTime: voteTime || 3,
      createdBy,
      createdAt: serverTimestamp(),
      baseStoryId,
      parentNodeId,
      firstNodeId: resumeFirstNodeId || null,
      password: hashedPassword, // ✅ ハッシュ済みで保存
    });

    // 最初のノード作成
    const nodeRef = doc(collection(storyRef, "nodes"));
    await setDoc(nodeRef, {
      content,
      createdBy,
      createdAt: serverTimestamp(),
      order: 0,
      adoptedFrom: resumeFirstNodeId || null,
      votes: 0,
      voters: [],
      status: "in-progress",
      password: hashedPassword, // ✅ ノード側もハッシュを統一
    });

    // stories に firstNodeId を更新
    const firstNodeId = nodeRef.id;
    await updateDoc(storyRef, { firstNodeId, updatedAt: serverTimestamp() });

    return { storyId, firstNodeId };
  } catch (error) {
    console.error("スレッド作成失敗:", error);
    throw error;
  }
};

/**
 * 投票結果を nodes に保存
 * 採用/没ノード処理 + story の currentRound/currentContent 更新
 */
export const saveVoteResultAsNodes = async (
  storyId,
  parentId,
  choices,
  chosenOption,
  round
) => {
  if (!storyId || !parentId || !choices) return;

  try {
    // ノード更新
    const nodePromises = choices.map(async (choice) => {
      const nodeRef = doc(db, "stories", storyId, "nodes", choice.id);

      if (choice.id === chosenOption?.id) {
        // 採用ノードは status 配列に storyId を追加
        await updateDoc(nodeRef, {
          status: arrayUnion(storyId),
          votes: choice.votes || 0,
          round,
          updatedAt: serverTimestamp(),
        });
      } else {
        // 没ノードは votes 更新のみ
        await updateDoc(nodeRef, {
          votes: choice.votes || 0,
          round,
          updatedAt: serverTimestamp(),
        });
      }

      //console.log(`ノード更新: ${choice.text} ID:${choice.id}`);
      return choice.id;
    });

    await Promise.all(nodePromises);

    // story ドキュメント更新（currentRound と currentContent）
    const storyRef = doc(db, "stories", storyId);
    await updateDoc(storyRef, {
      currentRound: round + 1,
      currentContent: chosenOption?.text || "",
      updatedAt: serverTimestamp(),
    });

    /*console.log(
      `story 更新: currentRound=${round}, currentContent=${chosenOption?.text}`
    );*/
  } catch (error) {
    /*
    console.log("choices:", choices);
    console.log("chosenOption:", chosenOption);
    console.error("saveVoteResultAsNodes エラー:", error);
    */
    throw error;
  }
};

/**
 * 分岐ストーリー作成時に更新する用
 * 選択された選択肢以下にstatus:storyIdを追加
 */

export const addBranchStoryStatus = async (
  storyId,
  nodesToUpdate,
  newStoryId
) => {
  if (!storyId || !nodesToUpdate || !newStoryId) return;

  const promises = nodesToUpdate.map(async (node) => {
    const nodeRef = doc(db, "stories", storyId, "nodes", node.id);
    await updateDoc(nodeRef, {
      status: arrayUnion(newStoryId),
      updatedAt: serverTimestamp(),
    });
  });

  await Promise.all(promises);
  //console.log(`分岐用ステータスを更新: 新storyId=${newStoryId}`);
};

/**
 * 没ノードをまとめて保存
 */
export const saveDiscardedNodes = async (
  storyId,
  discardedNodes,
  round = 1
) => {
  if (!storyId || !discardedNodes || discardedNodes.length === 0) return;

  const promises = discardedNodes.map((node) =>
    addDoc(collection(db, "stories", storyId, "nodes"), {
      ...node,
      status: "discarded",
      round,
      createdAt: serverTimestamp(),
    })
  );

  await Promise.all(promises);
};

/**
 * コメントを追加する
 * @param {Object} param0 
 * @param {string} param0.nodeId
 * @param {string} param0.userId
 * @param {string} param0.content
 * @param {string|null} param0.mediaUrl
 * @returns {Promise} Firestore docRef
 */
export const addComment = async ({ nodeId, userId, content, mediaUrl = null }) => {
  try {
    const commentsCol = collection(db, "nodes", nodeId, "comments");
    const docRef = await addDoc(commentsCol, {
      userId,
      content,
      mediaUrl,
      createdAt: serverTimestamp(),
    });
    return docRef.id; // 🔹 コメントIDだけ返す
  } catch (err) {
    console.error("Failed to add comment:", err);
    throw err;
  }
};


/**
 * コメントを購読する
 * @param {string} nodeId 
 * @param {function} setComments 
 * @returns unsubscribe function
 */
export const subscribeComments = (nodeId, setComments) => {
  const commentsCol = collection(db, "nodes", nodeId, "comments");
  const q = query(commentsCol, orderBy("createdAt", "asc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const comments = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setComments(comments);
  });

  return unsubscribe;
};

/**
 * ノード取得
 */
export const getNode = async (storyId, nodeId) => {
  try {
    const docRef = doc(db, "stories", storyId, "nodes", nodeId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error("getNode エラー:", error);
    throw error;
  }
};

/**
 * スレッド取得
 */
export const getThread = async (storyId) => {
  try {
    const docRef = doc(db, "stories", storyId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error("getThread エラー:", error);
    throw error;
  }
};

/**
 * 図書館ページで使用Firestore から全スレッドを取得
 */
export const getThreadList = async () => {
  const querySnapshot = await getDocs(collection(db, "stories"));

  const threads = await Promise.all(
    querySnapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      let firstNodeId = null;

      try {
        // ルートノードを stories/{storyId}/nodes から探す
        const nodesSnap = await getDocs(
          collection(db, "stories", docSnap.id, "nodes")
        );
        const nodes = nodesSnap.docs.map((n) => ({ id: n.id, ...n.data() }));

        // 条件に応じて最初のノードを決める（例: createdAt が一番古いもの）
        if (nodes.length > 0) {
          const sorted = nodes.sort(
            (a, b) => a.createdAt?.seconds - b.createdAt?.seconds
          );
          firstNodeId = sorted[0].id;
        }
      } catch (err) {
        console.warn("ノード取得エラー:", err);
      }

      return { id: docSnap.id, firstNodeId, ...data };
    })
  );

  return threads;
};

/**
 * nodes: Firestore から取得したノード配列
 * 例: [{ id, text, parentId, childrenIds, round, status }, ...]
 */
export const buildTreeFromNodes = (nodes) => {
  const nodeMap = {};

  // すべてのノードをマッピング
  nodes.forEach((n) => {
    nodeMap[n.id] = {
      id: n.id,
      name: n.text || "無題",
      children: [],
      parentIdForClick: n.parentId, // ← クリック用にコピー
      round: n.round,
      status: n.status,
    };
  });

  // childrenIds に従って親ノードに紐付け
  nodes.forEach((n) => {
    if (n.childrenIds && n.childrenIds.length > 0) {
      n.childrenIds.forEach((childId) => {
        if (nodeMap[childId]) {
          nodeMap[n.id].children.push(nodeMap[childId]);
        }
      });
    }
  });

  // 親がいないノードをルートとして返す
  const roots = nodes.filter((n) => !n.parentId).map((n) => nodeMap[n.id]);

  return roots.length === 1 ? roots[0] : { name: "ROOT", children: roots };
};

// ノードID からスレッド情報を取得
export const getThreadByNodeId = async (nodeId) => {
  //e.log("🔍 getThreadByNodeId で検索中 nodeId:", nodeId);

  const storiesRef = collection(db, "stories");
  const q = query(storiesRef, where("firstNodeId", "==", nodeId));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    throw new Error(`ノードが見つかりません: ${nodeId}`);
  }

  const docSnap = querySnapshot.docs[0];
  const data = docSnap.data();

  //console.log("✅ 見つかったスレッド:", docSnap.id, data);

  return { id: docSnap.id, ...data };
};

// 既存カテゴリを取得
export const getCategories = async () => {
  const q = collection(db, "categories");
  const snapshot = await getDocs(q);
  const categories = snapshot.docs.map((doc) => doc.data().name);
  return categories;
};

// 新しいカテゴリを追加
export const addCategory = async (name) => {
  const categoriesRef = collection(db, "categories");
  await addDoc(categoriesRef, { name });
};

// ノードをFirestoreに追加
export const addNodeToFirestore = async (storyId, nodeData) => {
  const ref = await addDoc(
    collection(db, "stories", storyId, "nodes"),
    nodeData
  );
  return ref;
};

// 親ノードのchildrenIdsを更新
export const updateParentChildrenIds = async (
  storyId,
  parentId,
  childrenIds
) => {
  const parentRef = doc(db, "stories", storyId, "nodes", parentId);
  await updateDoc(parentRef, {
    childrenIds: childrenIds,
  });
};

/**
 * 再開スレッドのラウンド2へ移行時に
 * parentIDとchildrenIDを更新する
 * @param {string} baseStoryId 元スレッドのStory ID
 * @param {string} parentNodeId 元スレッドの選択肢ノードID
 * @param {string} newStoryId 再開スレッドのStory ID
 */
export const linkRestartThread = async (
  baseStoryId,
  parentNodeId,
  newStoryId
) => {
  try {
    // ✅ 再開スレッドの最初のノードを取得
    const newNodesRef = doc(db, `stories/${newStoryId}/nodes`);
    const newNodeSnap = await getDoc(newNodesRef);

    if (!newNodeSnap.exists()) {
      console.warn("再開スレッドの最初のノードが見つかりません");
      return;
    }

    const firstNodeId = newNodeSnap.id;

    // ✅ 1. 再開スレッドの最初のノードに parentNodeId を追加
    const firstNodeRef = doc(db, `stories/${newStoryId}/nodes/${firstNodeId}`);
    await updateDoc(firstNodeRef, {
      parentNodeId: parentNodeId,
    });

    // ✅ 2. 元スレッドの選択肢ノードに childrenIds を追加
    const parentNodeRef = doc(
      db,
      `stories/${baseStoryId}/nodes/${parentNodeId}`
    );
    const parentSnap = await getDoc(parentNodeRef);

    if (!parentSnap.exists()) {
      console.warn("親ノードが見つかりません");
      return;
    }

    await updateDoc(parentNodeRef, {
      childrenIds: arrayUnion(firstNodeId),
    });

    /*console.log(
      `✅ 再開スレッド連結完了: 元(${baseStoryId}/${parentNodeId}) → 再開(${newStoryId}/${firstNodeId})`
    );
    */
  } catch (err) {
    console.error("❌ 再開スレッドリンク処理失敗:", err);
  }
};

// ────────── パスワードハッシュ機能 ──────────
/**
 * パスワードをハッシュ化
 * @param {string} password 平文のパスワード
 * @returns {Promise<string>} ハッシュ済みパスワード
 */
export async function hashPassword(password) {
  const salt = await bcrypt.genSalt(10);
  const hashed = await bcrypt.hash(password, salt);
  return hashed;
}

/**
 * Firestore にユーザーをハッシュ済みで保存
 * @param {string} userId FirestoreドキュメントID
 * @param {string} password 平文パスワード
 * @param {object} additionalData 他のユーザーデータ
 */
export async function saveUserWithHashedPassword(
  userId,
  password,
  additionalData = {}
) {
  try {
    const hashedPassword = await hashPassword(password);
    await setDoc(doc(db, "users", userId), {
      password: hashedPassword,
      createdAt: new Date(),
      ...additionalData,
    });
    //console.log("✅ ハッシュ済みパスワードをFirestoreに保存しました");
  } catch (error) {
    console.error("❌ ユーザー保存時にエラー:", error);
    throw error;
  }
}

/**
 * Firestore に保存されたハッシュと入力パスワードを比較
 * @param {string} userId FirestoreドキュメントID
 * @param {string} inputPassword 入力パスワード（平文）
 * @returns {Promise<boolean>} 一致すれば true
 */
export async function verifyUserPassword(userId, inputPassword) {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.warn("⚠ ユーザーが存在しません");
      return false;
    }

    const userData = userSnap.data();
    const isMatch = await bcrypt.compare(inputPassword, userData.password);
    return isMatch;
  } catch (error) {
    console.error("❌ パスワード検証エラー:", error);
    throw error;
  }
}

/**
 * ✅ verifyStoryPassword
 * スレッドのパスワード認証を行う
 * @param {string} storyId Firestore上のストーリーID
 * @param {string} inputPassword ユーザーが入力したパスワード（平文）
 * @returns {boolean} 認証成功なら true、失敗なら false
 */
export const verifyStoryPassword = async (storyId, inputPassword) => {
  try {
    const storyRef = doc(db, "stories", storyId);
    const storySnap = await getDoc(storyRef);

    if (!storySnap.exists()) {
      console.warn(
        "verifyStoryPassword: 該当ストーリーが存在しません",
        storyId
      );
      return false;
    }

    const storyData = storySnap.data();
    //console.log("取得したストーリーID:", storySnap.id);
    //console.log("取得したストーリー全データ:", storyData);
    //console.log("password フィールドの型:", typeof storyData.password);

    const storedHash = storyData.password;

    if (!storedHash) {
      console.warn("verifyStoryPassword: パスワードが設定されていません");
      return true; // パスワード未設定は常にOK
    }

    //console.log("入力パスワード:", inputPassword);
    //console.log("Firestore のハッシュ:", storedHash);

    const isMatch = await bcrypt.compare(inputPassword.trim(), storedHash);


    //console.log("照合結果:", isMatch);
    return isMatch;
  } catch (err) {
    console.error("verifyStoryPassword error:", err);
    return false;
  }
};

//----
